import { useMemo, useState } from "react";
import { Collapse } from "react-collapse";

import useUserStore from "@store/useUserStore";
import useMeetingStore from "@store/useMeetingStore";
import { useSwitches } from "@hooks/useSwitches";
import { useDebounce } from "@hooks/useDebounce";
import { useUsers } from "@components/TeamList/hooks/useUsers";
import { useUpdateMeeting } from "@screens/meeting/hooks/useUpdateMeeting";
import { useAddMeetingAttendee } from "@screens/meeting/hooks/useAddMeetingAttendee";
import { useRemoveMeetingAttendee } from "@screens/meeting/hooks/useRemoveMeetingAttendee";
import { useSearchPeople } from "@components/TeamList/hooks/useSearchPeople";
import { useActivatePerson } from "@components/TeamList/hooks/useActivatePerson";
import { getSelectedOrUnselectedAttendees } from "@utils/meeting.util";
import { convertUsersToUsersForDropdown } from "@utils/users.util";
import { getFullNameOrEmail } from "@utils/tags.util";
import { cn } from "@utils/tailwind.util";
import { User, UserForDropdown } from "@app-types/common.type";
import { IMeetingAttendeePayload } from "@interfaces/meeting.interface";
import { IPeople } from "@interfaces/people.interface";
import { openPersonPage } from "@utils/navigation.util";
import { CollapsibleSectionHeader } from "@components/Headers/CollapsibleSectionHeader";
import { TeamList } from "@components/TeamList/TeamList";
import { AttendeesDropdown } from "./AttendeesDropdown";

interface IAttendeesSectionProps {
  attendees: User[];
  people: IPeople[];
  size?: "small" | "medium";
  className?: string;
}

export const AttendeesSection = ({
  attendees,
  people,
  size = "small",
  className,
}: IAttendeesSectionProps) => {
  const [searchValueAttendeesDropdown, setSearchValueAttendeesDropdown] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValueAttendeesDropdown, 500);

  const { activeUser } = useUserStore();
  const { meetingId } = useMeetingStore();
  const { data: allUsers } = useUsers();
  const { data: allPeople } = useSearchPeople(debouncedSearchValue);
  const { mutate: updateMeetingMutate } = useUpdateMeeting();
  const { mutate: addMeetingAttendeeMutate } = useAddMeetingAttendee();
  const { mutate: removeMeetingAttendeeMutate } = useRemoveMeetingAttendee();
  const { mutate: activatePersonMutate } = useActivatePerson();

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { isAvailable } = useSwitches();
  const enableCompaniesAndPeople = isAvailable("COMPANIES_AND_PEOPLE");

  const countOfAttendees = attendees.length + people.length;

  const usersOptions: UserForDropdown[] = useMemo(
    () => convertUsersToUsersForDropdown(allUsers?.results, activeUser),
    [allUsers, activeUser]
  );

  const peopleOptions: UserForDropdown[] = useMemo(() => {
    if (!allPeople) return [];

    const parsedUsers: UserForDropdown[] =
      allPeople?.results.map((user) => ({
        ...user,
        value: user.id || "",
        label: getFullNameOrEmail(user as unknown as User),
        type: "people",
        timezone: "",
        title: null,
      })) || [];

    return parsedUsers;
  }, [allPeople, activeUser]);

  const handleToggleSection = () => setIsOpen((prevState) => !prevState);

  const handleOpenDropdown = () => setIsDropdownOpen(true);
  const handleCloseDropdown = () => setIsDropdownOpen(false);

  const handleUpdateAttendees = (user: UserForDropdown) => {
    const peopleIds = people.map((person) => person.id);
    let attendeeIds = attendees.map((attendee) => attendee.id);
    const newPeople = [...people];
    let newAttendees = [...attendees];
    let payload: IMeetingAttendeePayload = {};

    if (user.id === "selectAllOption") {
      newAttendees = getSelectedOrUnselectedAttendees(attendees, usersOptions);
      attendeeIds = newAttendees.map((attendee) => attendee.id);

      updateMeetingMutate({
        payload: {
          id: meetingId,
          attendees: attendeeIds,
          external_attendees: [],
          people: peopleIds,
        },
        attendees: newAttendees,
        people: newPeople,
      });
    } else {
      // If the attendee is not the active user and there are no attendees or people yet, then add active user.
      // activeUser is typed as IUser | null in the store and defaults to null until the auth restore completes —
      // guard with optional chaining so a race between attendee interaction and user fetch doesn't crash render.
      if (user.id !== activeUser?.id && attendees.length === 0 && people.length === 0) {
        const userMe = usersOptions.find((member) => member.id === activeUser?.id);

        if (userMe) {
          newAttendees.push(userMe);
          addMeetingAttendeeMutate({
            payload: { user: userMe.id },
            attendees: newAttendees,
            people: newPeople,
          });
        }
      }

      if (user.type === "people") {
        newPeople.push(user as unknown as IPeople);
        payload.person = user.id;
      } else if (user.type === "team members") {
        newAttendees.push(user);
        payload.user = user.id;
      } else {
        newPeople.push(user as unknown as IPeople);
        payload.external_attendee = user.id;
      }

      addMeetingAttendeeMutate({ payload, attendees: newAttendees, people: newPeople });
    }
  };

  const handleDeleteAttendee = (userId: string) => {
    let payload: IMeetingAttendeePayload = {};
    const newPeople = people.filter((person) => person.id !== userId);
    const newAttendees = attendees.filter((attendee) => attendee.id !== userId);

    if (people.some((u) => u.id === userId)) {
      payload.person = userId;
    } else if (attendees.some((u) => u.id === userId)) {
      payload.user = userId;
    }

    removeMeetingAttendeeMutate({ payload, attendees: newAttendees, people: newPeople });
  };

  const handlePersonClick = (person: IPeople) => {
    if (person.is_suggested) {
      activatePersonMutate(person);
    } else {
      openPersonPage(person.id);
    }
  };

  const isAllMembersSelected = attendees.length === usersOptions.length;

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <CollapsibleSectionHeader
        title="Attendees"
        tagsCount={countOfAttendees}
        isOpen={isOpen}
        onToggleSection={handleToggleSection}
        onOpenDropdown={handleOpenDropdown}
      />

      <Collapse isOpened={isOpen}>
        <AttendeesDropdown
          options={enableCompaniesAndPeople ? [...usersOptions, ...peopleOptions] : usersOptions}
          attendees={attendees}
          people={people}
          onDeleteAttendee={handleDeleteAttendee}
          onAddAttendee={handleUpdateAttendees}
          isDropdownOpen={isDropdownOpen}
          onCloseDropdown={handleCloseDropdown}
          searchValueAttendeesDropdown={searchValueAttendeesDropdown}
          setSearchValueAttendeesDropdown={setSearchValueAttendeesDropdown}
          enableCompaniesAndPeople={enableCompaniesAndPeople}
          isAllMembersSelected={isAllMembersSelected}
        />

        <TeamList
          attendees={attendees}
          people={people}
          onDelete={handleDeleteAttendee}
          onClick={handlePersonClick}
          size={size}
          erasable
          compactErasable
        />
      </Collapse>
    </section>
  );
};
